import { createContext, useContext, useEffect, useState } from 'react'
import { supabase, initialAuthAction } from '../lib/supabase'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [profile, setProfile] = useState(null)
  const [brandAccess, setBrandAccess] = useState([])
  const [brands, setBrands] = useState([])
  const [activeBrand, setActiveBrand] = useState(null)
  const [loading, setLoading] = useState(true)
  const [viewAsBrandAdmin, setViewAsBrandAdminState] = useState(false)
  // True when the user arrived via an invite or password-recovery link and must
  // set a password before using the app.
  const [passwordResetMode, setPasswordResetMode] = useState(
    initialAuthAction?.type === 'invite' || initialAuthAction?.type === 'recovery'
  )

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) {
        setViewAsBrandAdminState(localStorage.getItem(`viewAsBrandAdmin:${session.user.id}`) === '1')
        loadUserData(session.user)
      } else setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      // Recovery links fire a dedicated event; force the Set Password screen.
      if (event === 'PASSWORD_RECOVERY') setPasswordResetMode(true)
      setUser(session?.user ?? null)
      if (session?.user) {
        setViewAsBrandAdminState(localStorage.getItem(`viewAsBrandAdmin:${session.user.id}`) === '1')
        loadUserData(session.user)
      } else {
        setProfile(null)
        setBrandAccess([])
        setBrands([])
        setActiveBrand(null)
        setViewAsBrandAdminState(false)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function loadUserData(user) {
    try {
      const [{ data: prof }, { data: allBrands }, { data: access }] = await Promise.all([
        supabase.from('user_profiles').select('*').eq('id', user.id).single(),
        supabase.from('brands').select('*').order('name'),
        supabase.from('user_brand_access').select('brand_id').eq('user_id', user.id)
      ])

      setProfile(prof)
      setBrands(allBrands || [])

      const savedBrandId = localStorage.getItem(`activeBrand:${user.id}`)

      if (prof?.role === 'super_admin') {
        setBrandAccess((allBrands || []).map(b => b.id))
        const saved = savedBrandId && allBrands?.find(b => b.id === savedBrandId)
        setActiveBrand(saved || allBrands?.[0] || null)
      } else {
        const accessIds = (access || []).map(a => a.brand_id)
        setBrandAccess(accessIds)
        const accessible = (allBrands || []).filter(b => accessIds.includes(b.id))
        const saved = savedBrandId && accessible.find(b => b.id === savedBrandId)
        setActiveBrand(saved || accessible[0] || null)
      }
    } catch (e) {
      console.error(e)
    } finally {
      setLoading(false)
    }
  }

  const actualRole = profile?.role
  const overrideActive = viewAsBrandAdmin && (actualRole === 'super_admin' || actualRole === 'brand_super_admin')
  const effectiveProfile = overrideActive ? { ...profile, role: 'brand_admin' } : profile

  const accessibleBrands = overrideActive
    ? (activeBrand ? [activeBrand] : [])
    : brands.filter(b => actualRole === 'super_admin' || brandAccess.includes(b.id))

  function switchActiveBrand(brand) {
    if (brand && user) localStorage.setItem(`activeBrand:${user.id}`, brand.id)
    setActiveBrand(brand)
  }

  function setViewAsBrandAdmin(value) {
    const next = Boolean(value)
    if (user) localStorage.setItem(`viewAsBrandAdmin:${user.id}`, next ? '1' : '0')
    setViewAsBrandAdminState(next)
  }

  async function signIn(email, password) {
    return supabase.auth.signInWithPassword({ email, password })
  }

  async function signOut() {
    try { localStorage.removeItem('inv_last_activity') } catch (e) { /* ignore */ } // idle sign-out clock (IdleSignOut)
    return supabase.auth.signOut()
  }

  // Set the user's password (used for invite acceptance & password recovery),
  // then clear reset mode and continue any loaded session data.
  async function setPassword(password) {
    const { data, error } = await supabase.auth.updateUser({ password })
    if (!error) {
      setPasswordResetMode(false)
      if (data?.user) loadUserData(data.user)
    }
    return { error }
  }

  // Send a password reset email. The link points back at /set-password, a path
  // we control — so it works regardless of Supabase's implicit/PKCE flow.
  async function resetPassword(email) {
    return supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/set-password`
    })
  }

  return (
    <AuthContext.Provider value={{
      user, profile: effectiveProfile, actualRole, brands, accessibleBrands, activeBrand,
      setActiveBrand: switchActiveBrand, loading, signIn, signOut,
      viewAsBrandAdmin: overrideActive, setViewAsBrandAdmin,
      passwordResetMode, setPassword, resetPassword, authActionError: initialAuthAction?.error || null
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
